import React, { useState, useRef } from "react";
import styles from './styles.module.scss';
import moment from "moment";

const VehicleDetails = ({ vehicleData }) => {
  const [activeTab, setActiveTab] = useState("compliance");
  
  const complianceRef = useRef(null);
  const datesRef = useRef(null);
  const bodyRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTabClick = (tab, ref) => {
    setActiveTab(tab);
    scrollToSection(ref);
  };

  // Dummy data for demonstration - replace with actual vehicleData when available
  const vehicleInfo = {
    licencePlate: vehicleData?.licensePlate,
    puccNumber: vehicleData?.PUCCNumber,
    chassisNumber: vehicleData?.chassisNumber,
    engineNumber: vehicleData?.engineNumber,
    insuranceNumber: vehicleData?.insurancePolicy,
    permitNumber: vehicleData?.permitNumber,
    permitType: vehicleData?.permitType,
    emissionNorms: vehicleData?.norms,
    insuranceValidity: vehicleData?.insuranceValidity ? moment(vehicleData?.insuranceValidity).format('DD MMM YYYY') : '',
    puccValidity: vehicleData?.PUCCValidity ? moment(vehicleData?.PUCCValidity).format('DD MMM YYYY') : '',
    fcValidity: vehicleData?.fitnessValidity ? moment(vehicleData?.fitnessValidity).format('DD MMM YYYY') : '',
    taxValidity: vehicleData?.taxValidity ? moment(vehicleData?.taxValidity).format('DD MMM YYYY') : '',
    rcValidity: vehicleData?.registrationValidUpto ? moment(vehicleData?.registrationValidUpto).format('DD MMM YYYY') : '',
    bodyType: vehicleData?.bodyType,
    cubicCapacity: vehicleData?.cubicCapacity,
    fuel: vehicleData?.fuel,
    manufacturer: vehicleData?.manufacturer,
    model: vehicleData?.model
  };

  const getDateStatus = (dateStr) => {
    if (!dateStr) return 'standard';
    
    // Parse the date string using moment (format: "DD MMM YYYY")
    const date = moment(dateStr, 'DD MMM YYYY');
    const today = moment().startOf('day');
    
    if (!date.isValid()) return 'standard';
    
    if (date.isBefore(today)) {
      return 'expired';
    } else {
      return 'valid';
    }
  };

  const shouldShowDocumentIcon = (fieldName) => {
    return ['insuranceValidity', 'fcValidity', 'licenceValidity'].includes(fieldName);
  };

  return (
    <div className={styles.vehicleDetailsContainer}>
      {/* Tab Navigation */}
      <div className={styles.vehicleDetailsTabs}>
        <button
          className={`${styles.tabButton} ${
            activeTab === 'compliance' ? styles.active : ''
          }`}
          onClick={() => handleTabClick('compliance', complianceRef)}
        >
          Compliance Details
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === 'dates' ? styles.active : ''
          }`}
          onClick={() => handleTabClick('dates', datesRef)}
        >
          Important Dates
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === 'body' ? styles.active : ''
          }`}
          onClick={() => handleTabClick('body', bodyRef)}
        >
          Body Details
        </button>
      </div>

      {/* Compliance Section */}
      <div
        ref={complianceRef}
        className={`${styles.detailsSection} ${styles.tableBtmMrg}`}
      >
        <h2 className={styles.sectionTitle}>Compliance Details</h2>
        <div className={styles.detailsTable}>
          {[
            ['Licence Plate', vehicleInfo.licencePlate],
            ['PUCC Number', vehicleInfo.puccNumber],
            ['Chassis Number', vehicleInfo.chassisNumber],
            ['Engine Number', vehicleInfo.engineNumber],
            ['Insurance Number', vehicleInfo.insuranceNumber],
            ['Permit Number', vehicleInfo.permitNumber],
            ['Permit Type', vehicleInfo.permitType],
            ['Emission Norms', vehicleInfo.emissionNorms],
          ].map(([label, value]) => (
            <div key={label} className={styles.tableRow}>
              <div className={`${styles.tableCell} ${styles.label}`}>
                {label}
              </div>
              <div className={`${styles.tableCell} ${styles.value}`}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Important Dates Section */}
      <div
        ref={datesRef}
        className={`${styles.detailsSection} ${styles.tableBtmMrg}`}
      >
        <h2 className={styles.sectionTitle}>Important Dates</h2>
        <div className={styles.detailsTable}>
          {[
            ['Insurance Validity', vehicleInfo.insuranceValidity],
            ['PUCC Validity', vehicleInfo.puccValidity],
            ['FC Validity', vehicleInfo.fcValidity],
            ['Tax Validity', vehicleInfo.taxValidity],
            ['RC Validity', vehicleInfo.rcValidity],
          ].map(([label, value]) => (
            <div key={label} className={styles.tableRow}>
              <div className={`${styles.tableCell} ${styles.label}`}>
                {label}
              </div>
              <div className={`${styles.tableCell} ${styles.value}`}>
                <span
                  className={`${styles.dateValue} ${
                    styles[
                      `date${
                        getDateStatus(value).charAt(0).toUpperCase() +
                        getDateStatus(value).slice(1)
                      }`
                    ]
                  }`}
                >
                  {value}
                </span>
                {shouldShowDocumentIcon(
                  label.replace(' ', '').toLowerCase(),
                ) && (
                  <i className={`far fa-file-pdf ${styles.documentIcon}`}></i>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Body Details Section */}
      <div ref={bodyRef} className={styles.detailsSection}>
        <h2 className={styles.sectionTitle}>Body Details</h2>
        <div className={styles.detailsTable}>
          {[
            ['Body Type', vehicleInfo.bodyType],
            ['Cubic Capacity', vehicleInfo.cubicCapacity],
            ['Fuel', vehicleInfo.fuel],
            ['Manufacturer', vehicleInfo.manufacturer],
            ['Model', vehicleInfo.model],
          ].map(([label, value]) => (
            <div key={label} className={styles.tableRow}>
              <div className={`${styles.tableCell} ${styles.label}`}>
                {label}
              </div>
              <div className={`${styles.tableCell} ${styles.value}`}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VehicleDetails;
